import { Injectable } from '@angular/core';
import { toKebabCase } from '../utils/string.utils';

/**
 * A single GTM container instance exposed on window.google_tag_manager[containerId].
 * Only the members this service interacts with are typed.
 */
interface GoogleTagManagerContainer {
  dataLayer?: {
    reset?: () => void;
  };
}

declare global {
  interface Window {
    dataLayer: unknown[];
    google_tag_manager: Record<string, GoogleTagManagerContainer>;
  }
}

/**
 * Extra dimensions that can be passed alongside a screen view event.
 * Used by staff-portal for staffing_season / application_type.
 */
export interface GtmScreenViewDimensions {
  staffing_season?: string;
  application_type?: string;
}

/**
 * Options for pushGTMEvent to support varied signatures across apps.
 */
export interface GtmEventOptions {
  includeAccount?: boolean;
  companyName?: string;
  gender?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GtmService {
  private _screenSlug = '';
  private _screenTitle = '';

  /**
   * Resets the dataLayer and all GTM containers.
   * Identical implementation across all customer-portal apps.
   */
  public resetGtm(): void {
    if (window.dataLayer !== undefined && window.google_tag_manager !== undefined) {
      window.dataLayer.length = 0;
      const gtmContainerReg = /GTM-/i;
      for (const gtmKey of Object.keys(window.google_tag_manager)) {
        const container = window.google_tag_manager[gtmKey];
        if (gtmContainerReg.test(gtmKey) && container.dataLayer && container.dataLayer.reset) {
          container.dataLayer.reset();
        }
      }
    }
  }

  /**
   * Pushes a view_screen event to the dataLayer.
   * @param screenSlug - The screen slug identifier
   * @param screenTitle - The screen title
   * @param dimensions - Optional extra dimensions (staffing_season, application_type)
   */
  public pushScreenView(screenSlug: string, screenTitle: string, dimensions?: GtmScreenViewDimensions): void {
    this.storeScreenContext(screenSlug, screenTitle);

    const dataLayerEvent = {
      'event': 'view_screen',
      'screen_slug': this._screenSlug,
      'screen_title': this._screenTitle,
      'staffing_season': dimensions?.staffing_season || undefined,
      'application_type': dimensions?.application_type || undefined,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataLayerEvent);
  }

  /**
   * Stores the current screen slug and title without pushing an event.
   * Used when the push is deferred (e.g. waiting for campaign URL resolution).
   * @param screenSlug - The screen slug identifier
   * @param screenTitle - The screen title
   */
  public storeScreenContext(screenSlug: string, screenTitle: string): void {
    this._screenSlug = toKebabCase(screenSlug);
    this._screenTitle = screenTitle;
  }

  /**
   * Pushes a custom GTM event to the dataLayer.
   * @param event - The event name
   * @param options - Optional configuration for account method and gender
   */
  public pushGTMEvent(event: string, options?: GtmEventOptions): void {
    window.dataLayer = window.dataLayer || [];
    const dataLayerObject: Record<string, string | undefined> = { event };
    if (options?.includeAccount) {
      dataLayerObject['method'] = options.companyName ? `${options.companyName} account` : undefined;
      dataLayerObject['gender'] = options.gender || undefined;
    }
    window.dataLayer.push(dataLayerObject);
  }

  /**
   * Returns the currently stored screen slug.
   */
  public get screenSlug(): string {
    return this._screenSlug;
  }

  /**
   * Returns the currently stored screen title.
   */
  public get screenTitle(): string {
    return this._screenTitle;
  }
}
